import { useCallback, useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { Icon, type IconName } from '../components/icons/Icon'
import { GlobalSearchOverlay } from '../components/search/GlobalSearchOverlay'
import { JobsTray } from '../components/jobs/JobsTray'
import { ThemeToggle } from '../theme/ThemeToggle'
import { UserMenu } from '../auth/UserMenu'
import { getUnreadCount } from '../api/notifications'
import { getUiConfig, type UiConfigContext } from '../api/identity'
import { BrandMark } from '../brand/BrandMark'
import { usePersistentState } from '../lib/usePersistentState'
import { ShellContextIndicator } from './ShellContextIndicator'
import './NavShell.css'

/**
 * IB-27 S5 (FR-M23.1 chrome half) — the left-nav collapse preference, per-browser `localStorage`
 * via the established `usePersistentState` hook, matching every sibling preference in this
 * codebase (theme/accent/dashboard-theme — design §2.2, DoR Q3 resolved).
 */
const NAV_COLLAPSED_STORAGE_KEY = 'iw.nav-collapsed'

interface NavItem {
  to: string
  label: string
  icon: IconName
}

interface NavGroup {
  label: string
  items: NavItem[]
}

/**
 * IA grouped by purpose (CLAUDE.md §4): Clients · Cases · My Work · Insights
 * · Data · Admin. Every leaf here is a real route — `Data` (`DataImportScreen`)
 * and `Admin` (`AdminSettingsScreen`) replaced their `ComingSoonStub` placeholders
 * once real backing reads existed; `ComingSoonStub` itself remains available for
 * any FUTURE leaf whose module genuinely has not shipped yet — never a dead nav
 * item, and never faked as more built than it is.
 *
 * `Branches` (IB-48 AC15) is a net-new nav surface, placed here per the
 * Tech Lead's recommended IA (design §4.2) — ratified by UX: it sits beside
 * `Legal entities` because `Branch` is a flat FK-child of `LegalEntity`
 * (same relationship shape as `Contact` under `Account`), so grouping every
 * CRM master-data entity under `Clients` keeps the group internally
 * consistent rather than splitting parents and children across groups.
 */
const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Clients',
    items: [
      { to: '/clients/accounts', label: 'Accounts', icon: 'building2' },
      { to: '/clients/legal-entities', label: 'Legal entities', icon: 'landmark' },
      { to: '/clients/branches', label: 'Branches', icon: 'map-pin' },
      { to: '/clients/contacts', label: 'Contacts', icon: 'users' },
    ],
  },
  { label: 'Cases', items: [{ to: '/cases', label: 'All cases', icon: 'briefcase' }] },
  {
    label: 'My Work',
    items: [
      { to: '/my-work', label: 'Work queue', icon: 'activity' },
      { to: '/my-work/notifications', label: 'Notifications', icon: 'bell' },
    ],
  },
  {
    label: 'Insights',
    items: [
      { to: '/insights/dashboards', label: 'Dashboards', icon: 'bar-chart' },
      { to: '/insights/dashboards/portfolio', label: 'Portfolio & executive overview', icon: 'bar-chart' },
      { to: '/insights/dashboards/jurisdictions', label: 'Jurisdiction & program analytics', icon: 'map-pin' },
      { to: '/insights/dashboards/operations', label: 'Operational / throughput analytics', icon: 'activity' },
      { to: '/insights/reports', label: 'Reports', icon: 'printer' },
    ],
  },
  {
    label: 'Data',
    items: [
      { to: '/data/import', label: 'Import & activity', icon: 'database' },
      { to: '/data/bulk', label: 'Bulk operations', icon: 'rows' },
    ],
  },
  // user-admin-entra-federation Slice B — the real Users screen sits alongside the product-lineage
  // Admin Settings; both nav leaves survive the v64 merge (keep-both-capabilities).
  {
    label: 'Admin',
    items: [
      { to: '/admin/users', label: 'Users', icon: 'users' },
      // IB-337 (≡ IB-88 ≡ IB-43) — OLS/FLS/UI-component + Role/Territory tree console, gated
      // server-side on security_config:F (distinct from the per-user admin capabilities /admin/users checks).
      { to: '/admin/security', label: 'Security Administration', icon: 'shield' },
      { to: '/admin/settings', label: 'Settings', icon: 'settings' },
      // IB-210 Slice 2 (demo v66) — Cartridge Authoring Studio (design doc §1). PlatformAdmin-only:
      // read endpoints are open canon (ADR-0017), but every mutation is fail-closed guarded to the
      // platform tenant server-side (`CartridgeService.rejectTenantCaller` et al.) — the same
      // server-enforced posture as every other Admin leaf here, no separate client-side role gate.
      { to: '/admin/cartridge-studio', label: 'Cartridge Studio', icon: 'edit-3' },
      // IB-289 (FR-M7.6 UI, IB-211 S6) — a DISTINCT tenant-facing leaf, deliberately separate from
      // Cartridge Studio above (platform-tenant-bound canon authoring, ADR-0017): a tenant user must
      // never be led to believe they are editing canon (ADR-0056 §3.1, design doc §3.1/R-33).
      { to: '/admin/eligibility-overlays', label: 'Eligibility overlays', icon: 'list-tree' },
    ],
  },
]

export function NavShell() {
  // Fetched once on shell mount — a best-effort badge, not a polling live-count (kept deliberately
  // simple per the story's "only if trivially wired... don't force it" guidance). A fetch failure
  // (e.g. logged-out, offline) just leaves the badge absent rather than surfacing an error here;
  // the Notifications screen itself is the source of truth and has its own real error state.
  const [unreadCount, setUnreadCount] = useState<number | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)

  // Persisted as `unknown` deliberately, then narrowed with `=== true` — a corrupted or
  // wrong-typed stored value (AC3's failure path) resolves to `false` (expanded, the documented
  // default), never crashes the shell and never gets treated as "collapsed" just because a raw
  // value happened to be truthy.
  const [storedNavCollapsed, setStoredNavCollapsed] = usePersistentState<unknown>(
    NAV_COLLAPSED_STORAGE_KEY,
    false,
  )
  const navCollapsed = storedNavCollapsed === true
  const toggleNavCollapsed = useCallback(() => {
    setStoredNavCollapsed((prev: unknown) => !(prev === true))
  }, [setStoredNavCollapsed])

  // IB-27 S5 — the shell's tenant/region context for the topbar indicator. Fetched fresh on every
  // shell mount (same posture as the `components` fetch above/below) so a fresh session never
  // shows a previous session's cached value; a fetch failure just leaves the indicator absent.
  const [meContext, setMeContext] = useState<UiConfigContext | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    getUnreadCount(controller.signal)
      .then((v) => setUnreadCount(v.unread))
      .catch(() => setUnreadCount(null))
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    getUiConfig(controller.signal)
      .then((cfg) => setMeContext(cfg.context ?? null))
      .catch(() => setMeContext(null))
    return () => controller.abort()
  }, [])

  const closeSearch = useCallback(() => setSearchOpen(false), [])

  // A single keyboard shortcut ("/", GitHub/Slack-style) that opens Global Search from anywhere in
  // the app EXCEPT while the user is already typing in a text field/textarea/select (never hijacks
  // a "/" the user meant to type). The nav button below is the same entry point, always visible and
  // reachable by Tab — this shortcut is a bonus, not the only path in.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return
      event.preventDefault()
      setSearchOpen(true)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className={'iw-shell' + (navCollapsed ? ' iw-shell--nav-collapsed' : '')}>
      <a href="#iw-main-content" className="iw-skip-link">
        Skip to content
      </a>
      <header className="iw-topbar">
        <NavLink to="/" className="iw-logo" aria-label="IncentiWise home">
          <span className="iw-logo-mark">
            <BrandMark size={22} title="" />
          </span>
          IncentiWise
        </NavLink>
        <button
          type="button"
          className="iw-topbar-search"
          onClick={() => setSearchOpen(true)}
          aria-haspopup="dialog"
        >
          <Icon name="search" size={14} />
          <span>Search cases, accounts, contacts…</span>
          <span className="iw-topbar-search__shortcut" aria-hidden="true">
            /
          </span>
        </button>
        {searchOpen ? <GlobalSearchOverlay onClose={closeSearch} /> : null}
        <div className="iw-topbar-spacer" />
        {/* IB-27 S5 (FR-M23.1) — tenant/region indicator; renders nothing until/unless the
            session's own bound context resolves at least one of its fields. */}
        <ShellContextIndicator context={meContext} />
        {/* IB-27 S2 (FR-M23.11) — the personal Background Jobs tray. Global topbar chrome, same
            placement rationale as Global Search: every route renders inside this shell's
            `<Outlet/>`, so it is reachable from every workspace by construction. */}
        <JobsTray />
        <ThemeToggle />
        <UserMenu />
      </header>
      <div className="iw-body">
        <nav id="iw-leftnav" className="iw-leftnav" aria-label="Primary">
          {NAV_GROUPS.map((group) => (
            <div className="iw-nav-group" key={group.label}>
              <div className="iw-nav-heading">{group.label}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  aria-label={item.label}
                  className={({ isActive }) => 'iw-nav-item' + (isActive ? ' iw-nav-item--active' : '')}
                >
                  <Icon name={item.icon} size={16} />
                  <span className="iw-nav-item__label">{item.label}</span>
                  {item.to === '/my-work/notifications' && unreadCount ? (
                    <span className="iw-nav-badge" aria-label={`${unreadCount} unread notifications`}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  ) : null}
                </NavLink>
              ))}
            </div>
          ))}
          {/* Persistent "Help & User Guide" nav-footer entry, below the primary nav groups
              (Clients/Cases/My Work/Insights/Data/Admin) and above the collapse toggle. A real
              same-origin anchor (the guide is host-served static HTML at `/pm/userguide.html`
              alongside the SPA, not an SPA route) opened in a new tab so navigating to it never
              loses in-progress work in the current tab — CLAUDE.md §4 "every CTA opens a real
              page." Reuses the `.iw-nav-item` pattern so hover/active/focus and the
              collapsed-rail icon-only treatment all match every other nav leaf. */}
          <div className="iw-nav-footer">
            <a
              href="/pm/userguide.html"
              target="_blank"
              rel="noopener noreferrer"
              className="iw-nav-item"
              title="Help & User Guide"
              aria-label="Help & User Guide"
            >
              <Icon name="book-open" size={16} />
              <span className="iw-nav-item__label">Help & User Guide</span>
            </a>
          </div>
          {/* IB-27 S5 — collapse is a pure visual-density change (rail-not-hide): NAV_GROUPS is
              untouched, every route stays reachable, and every collapsed icon keeps its
              `title`/`aria-label` accessible name above. */}
          <button
            type="button"
            className="iw-leftnav-toggle"
            onClick={toggleNavCollapsed}
            aria-expanded={!navCollapsed}
            aria-controls="iw-leftnav"
            aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <Icon name={navCollapsed ? 'chevron-right' : 'chevron-left'} size={16} />
            {navCollapsed ? null : <span>Collapse</span>}
          </button>
        </nav>
        <main id="iw-main-content" className="iw-main">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
